import * as Sentry from '@sentry/browser';
import versionService from 'commons/versionService/versionService';
import store from 'commons/store/Store.js';
import statisticsService from 'commons/api/statisticsService/statisticsService';
import { getExternalLinks } from 'commons/externalLinks/externalLinks';
import pdvService from 'commons/partnerDataValidation/service/pdvService';
import OnboardingDialog from 'src/app/components/onboarding/OnboardingDialog.vue';
import dialogService from 'commons/dialogs/wrapper/dialogService';
import eventBus from 'commons/eventBus/eventBus';
import { initializeTranslations } from 'commons/translate/i18n';
import router from 'src/app/router/router';
import analyticsService from 'commons/tracking/analytics';
import RecapService from 'commons/dialogs/recap/RecapService';
import { useStatisticsStore } from 'commons/stores/statistics';
import { useSmartFaqStore } from 'commons/stores/smartFaq';
import { useApplicationStore } from 'commons/stores/application';
import { useUserAddress } from '../commons/stores/user/userAddress';
import { useDialogsStore } from 'commons/stores/dialogs';
import { useOnboardingStore } from 'commons/stores/onboarding';
import { watch } from 'vue';

/**
 * Keeps a single failing call from aborting the login init.
 *
 * Every step after a rejected `await` used to be skipped silently, which left
 * the publishing and settings data unloaded and froze the pages waiting on it.
 * See DEV-231297.
 */
function settle(label, promise) {
  return Promise.resolve(promise).catch((error) => {
    console.error(`[init] ${label} failed`, error);
  });
}

// Rest args rather than (action, payload) so a payload-less dispatch stays a
// single-argument call.
function settleDispatch(...dispatchArgs) {
  return settle(dispatchArgs[0], store.dispatch(...dispatchArgs));
}

export default {
  async onAppStart() {
    initializeTranslations({
      displayKeys: window.location?.href?.includes(
        '?displayTranslationKeys=true',
      ),
    });
    const applicationStore = useApplicationStore();

    settleDispatch('platform/fetchCurrencies');
    settleDispatch('platform/fetchMetaColors');
    applicationStore.processUrlParams();

    eventBus.$on('shoparea:shopUpdate', () => {
      settleDispatch('user/fetchShops');
      settleDispatch('user/fetchPointsOfSale');
    });

    this.watchForOnboardingChanges();

    if (!SPRD?.LOCAL_RUNTIME) {
      versionService.startVersionCheck();
    }
  },
  async watchForOnboardingChanges() {
    const onboardingStore = useOnboardingStore();

    watch(
      () => onboardingStore.finishedRelevantSteps,
      (newState, oldState) => {
        const newSteps = newState.steps;
        const oldSteps = oldState.steps;

        if (
          newState.scope === oldState.scope &&
          oldSteps !== null &&
          newSteps !== null &&
          oldSteps.length < newSteps.length
        ) {
          if (useDialogsStore().dialogCount > 0) {
            const openOnboardingDialogAfterOtherDialogsAreRemoved = () => {
              eventBus.$off(
                'dialogs:removed',
                openOnboardingDialogAfterOtherDialogsAreRemoved,
              );
              dialogService.openDialog(OnboardingDialog, {
                oldSteps,
                newSteps,
              });
            };
            eventBus.$on(
              'dialogs:removed',
              openOnboardingDialogAfterOtherDialogsAreRemoved,
            );
          } else {
            dialogService.openDialog(OnboardingDialog, { oldSteps, newSteps });
          }
        }
      },
    );
  },
  async onUserLogin() {
    if (store.state.user.data.guest) {
      store.dispatch('user/logout', {
        redirectTargetAfterLogout: getExternalLinks().START_SELLING,
      });
      return;
    }

    if (SPRD.ERROR_LOGGING_ACTIVE) {
      Sentry.configureScope(function (scope) {
        scope.setUser({ id: store.state.user.data.id });
      });
    }
    analyticsService.setUserId();

    const statisticsStore = useStatisticsStore();
    const smartFaqStore = useSmartFaqStore();
    const applicationStore = useApplicationStore();
    const userAddress = useUserAddress();

    settleDispatch('platform/fetchShippingStates');
    settleDispatch('platform/fetchShippingCountries');
    settleDispatch('platform/fetchLanguages');
    settleDispatch('platform/fetchCountries');
    await settle('userAddress.fetchCountries', userAddress.fetchCountries());
    settleDispatch('settings/fetchSettings');
    settle('smartFaqStore.init', smartFaqStore.init());

    // Settled individually: a single failing call must not abort the rest of
    // the init (DEV-231297).
    await Promise.all([
      settle('settings.onDataLoaded', store.state.settings.onDataLoaded),
      settleDispatch('user/fetchShops'),
      settleDispatch('userPaymentInfo/fetchPaymentInfo'),
      settleDispatch('userTaxation/fetchTaxationData'),
      settleDispatch('userTrolley/fetchTrolleyStatus'),
      settle('userAddress.fetchAddressData', userAddress.fetchAddressData()),
      settleDispatch('ideas/checkIdeaIntents'),
      settle(
        'statistics.fetchLifetimeStatistics',
        statisticsStore.fetchLifetimeStatistics(),
      ),
    ]);

    if (store.getters['user/isAdmin']) {
      analyticsService.disable();
    }

    if (applicationStore.isMobileApp) {
      return;
    }

    if (store.getters['settings/publishingCoreEnabled']) {
      await Promise.all([
        settleDispatch('publishingCore/fetchPublishingIntents'),
        settleDispatch('publishingCore/fetchData'),
      ]);
    }

    settleDispatch('user/updateMfaStatus');
    useOnboardingStore().initialize();

    const registrationRoutes = [
      'partnerarea.register',
      'partnerarea.shopRegister',
      'partnerarea.themes',
    ];

    if (
      !registrationRoutes.includes(
        router.currentRoute?.value?.meta?.previousState?.name,
      ) &&
      !registrationRoutes.includes(router.currentRoute?.value?.name)
    ) {
      if (
        !versionService.getLastVisitedVersion() &&
        !versionService.getLastVisitedSpreadAppVersion()
      ) {
        // new partner registered outside PA and SpreadApp

        // type 'BOTH' should not be possible for a partner first visiting PA, so its most likely a shop partner
        if (store.getters['user/partnerType'] === 'BOTH') {
          settleDispatch('settings/setSetting', {
            key: 'MP_ENABLED',
            value: false,
          });
        }

        // CentralAuth's post-registration handler never ran for these partners
        // (the accounts flow can end in a redirect to /login instead of the
        // in-page success callback), so nobody put them on the new shop journey.
        // Without this they silently fall back to the PUBLISHING_JOURNEY_SHOP
        // default (FLEXI), i.e. the old journey.
        settleDispatch('settings/setSetting', {
          key: 'PUBLISHING_JOURNEY_SHOP',
          value: ['PUBC'],
        });

        dialogService.openDialog(OnboardingDialog, { welcome: true });
      } else if (!versionService.getLastVisitedVersion()) {
        // partner registered in SpreadApp, first visit in PA
        dialogService.openDialog(OnboardingDialog, { welcome: true });
      }
    }

    versionService.persistLastVisitedVersion();
    settleDispatch('user/fetchProductTypes');
    settleDispatch('platform/fetchProductTypeShares');
    settleDispatch('notifications/startPolling');
    settleDispatch('dms/startPolling');
    settleDispatch('metrics/clearCache');

    // Settled separately, not as one Promise.all: a failing credit overview
    // must not skip the address validation check, which matters far more
    // (DEV-231297).
    await Promise.all([
      settle(
        'statistics.getCreditOverview',
        statisticsService.getCreditOverview().then((creditOverview) => {
          store.commit(
            'account/setPayoutLimitReached',
            creditOverview ? creditOverview.payoutLimitReached : false,
          );
        }),
      ),
      settle(
        'pdv.fetchState',
        pdvService.fetchState().then((state) => {
          if (
            pdvService.getCurrentState(state) ===
            pdvService.STATES.CONFIRM_NEEDED
          ) {
            router.push({ name: 'partnerarea.addressValidation' });
          }
        }),
      ),
    ]);

    RecapService.checkForUnseenRecap();
  },
};
